import { gmailService } from '../../services/gmail'
import { useAuthStore } from '../../stores/authStore'
import type { Contact, Deal, Task, PipelineStage } from '../../types'
import { STAGE_COMPOSE_SUBJECTS } from '../../types'
import { Button } from '../ui'

interface ComposeButtonProps {
  contact: Contact
  deal?: Deal
  task?: Task
  variant?: 'primary' | 'secondary' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  className?: string
  label?: string
}

export default function ComposeButton({
  contact,
  deal,
  task,
  variant = 'secondary',
  size = 'sm',
  className = '',
  label,
}: ComposeButtonProps) {
  const { user } = useAuthStore()

  if (!contact.email) {
    return null
  }

  const firstName = contact.name.split(' ')[0]

  const buildEmailBody = (): string => {
    const lines: string[] = [`Hi ${firstName},`]
    lines.push('')

    if (task && task.title) {
      lines.push(`I wanted to follow up on ${task.title.toLowerCase()}.`)
      lines.push('')
    } else if (deal) {
      const contextualOpening = getContextualOpening(deal.stage, deal.title)
      if (contextualOpening) {
        lines.push(contextualOpening)
        lines.push('')
      }
    }

    lines.push('')
    lines.push('Best regards,')
    lines.push(user?.name || '')

    return lines.join('\n')
  }

  const buildSubject = (): string => {
    if (deal) {
      const baseSubject = STAGE_COMPOSE_SUBJECTS[deal.stage]
      if (deal.stage === 'proposal' || deal.stage === 'negotiation') {
        return `${baseSubject} for ${deal.title}`
      }
      return baseSubject
    }
    return ''
  }

  const handleCompose = () => {
    const url = gmailService.getGmailComposeUrl({
      to: contact.email,
      subject: buildSubject(),
      body: buildEmailBody(),
    })
    window.open(url, '_blank')
  }

  const buttonLabel = label || (task ? 'Follow up' : `Email ${firstName}`)

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleCompose}
      className={className}
      aria-label={`Compose email to ${contact.name}`}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="mr-1.5"
      >
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </svg>
      {buttonLabel}
    </Button>
  )
}

function getContextualOpening(stage: PipelineStage, dealTitle: string): string {
  switch (stage) {
    case 'lead':
      return "I'd like to introduce myself and learn more about how we might work together."
    case 'conversation':
      return "Thank you for taking the time to speak with me. I wanted to follow up on our conversation."
    case 'proposal':
      return `I wanted to follow up on the proposal for ${dealTitle} and see if you have any questions.`
    case 'negotiation':
      return `I wanted to check in on ${dealTitle} and see if there's anything else you need from me.`
    case 'won':
      return "Thank you for choosing to work with me. I'm looking forward to getting started."
    case 'lost':
    case 'nurture':
      return "I hope you're doing well. I wanted to check in and see how things are going."
    default:
      return ''
  }
}

interface ComposeFromTaskProps {
  task: Task
  contact: Contact
  variant?: 'primary' | 'secondary' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
}

export function ComposeFromTask({
  task,
  contact,
  variant = 'ghost',
  size = 'sm',
}: ComposeFromTaskProps) {
  if (!contact.email) {
    return null
  }

  return (
    <ComposeButton
      contact={contact}
      task={task}
      variant={variant}
      size={size}
      label="Follow up"
    />
  )
}

interface ComposeFromDealProps {
  deal: Deal
  contact: Contact
  variant?: 'primary' | 'secondary' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  showIcon?: boolean
}

export function ComposeFromDeal({
  deal,
  contact,
  variant = 'secondary',
  size = 'sm',
}: ComposeFromDealProps) {
  if (!contact.email) {
    return null
  }

  const getLabel = (): string => {
    switch (deal.stage) {
      case 'lead':
        return 'Send intro'
      case 'proposal':
        return 'Chase proposal'
      case 'negotiation':
        return 'Follow up'
      default:
        return 'Send follow-up'
    }
  }

  return (
    <ComposeButton
      contact={contact}
      deal={deal}
      variant={variant}
      size={size}
      label={getLabel()}
    />
  )
}
